
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { LoginForm } from "@/components/auth/LoginForm";
import { Spinner } from "@/components/ui/Spinner";
import { logger } from "@/services/LoggingService";

const Auth = () => {
  const { user, session, isInitializing, setWaitlistDialog, setAuthError } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  useEffect(() => {
    // Only redirect when both user and session are available and we're not in a special auth mode
    if (user && session && !mode) {
      const redirect = searchParams.get('redirect') || '/';
      navigate(redirect, { replace: true });
    }
  }, [user, session, navigate, mode, searchParams]);

  // Handle OAuth errors from provider callbacks
  useEffect(() => {
    if (error && !isInitializing) {
      logger.info('auth.oauth', 'OAuth error detected', { 
        error, 
        errorDescription,
        timestamp: new Date().toISOString()
      });

      if (error === 'access_denied') {
        // User cancelled OAuth - trigger waitlist enrollment
        const userEmail = searchParams.get('email') || '';
        
        logger.info('auth.oauth', 'User cancelled OAuth, triggering waitlist', { 
          error,
          userEmail,
          timestamp: new Date().toISOString()
        });

        if (userEmail) {
          setWaitlistDialog(userEmail);
        } else {
          // Show error message if we don't have user email
          setAuthError('Authentication was cancelled. Please try again or contact support if you need access.');
        }
      } else {
        // Other OAuth errors
        const friendlyMessage = errorDescription 
          ? `Authentication failed: ${errorDescription}`
          : 'Authentication failed. Please try again.';
        
        setAuthError(friendlyMessage);
        
        logger.error('auth.oauth', 'OAuth error occurred', {
          error,
          errorDescription,
          timestamp: new Date().toISOString()
        });
      }

      // Clear error parameters from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      newSearchParams.delete('error_description');
      newSearchParams.delete('email');
      
      const newUrl = newSearchParams.toString() 
        ? `${window.location.pathname}?${newSearchParams.toString()}`
        : window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [error, errorDescription, isInitializing, searchParams, navigate, setWaitlistDialog, setAuthError]);

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return <LoginForm />;
};

export default Auth;
